import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { DialogActions } from 'src/app/model/dictionary';
import { ALLOWED_IMAGE_TYPES } from '../../shared-components/upload/image-upload/image-upload.component';
import { FileApiService } from 'src/app/data-transfer/services/file-api-service';
import { SimpleAlertDialogComponent, SimpleDialogData } from '../simple-alert-dialog/simple-alert-dialog.component';
import { getDialogConfig } from 'src/app/util/dialog-util';

export const UPLOAD_TAB_INDEX = 0;
export const DRAW_TAB_INDEX = 1;

export class SignatureCaptureDialogData {
  constructor(
    public personName: string,
    public existingSignatureImageId: number | null = null,
    public showPersistenceChoice = false,
    public confirmButtonLabel?: string
  ) {}
}

@Component({
  selector: 'app-signature-capture-dialog',
  templateUrl: './signature-capture-dialog.component.html',
  styleUrls: ['./signature-capture-dialog.component.scss'],
})
export class SignatureCaptureDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('signatureCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  readonly allowedImageTypes = ALLOWED_IMAGE_TYPES;

  activeTab = DRAW_TAB_INDEX;
  saveToProfile = false;
  uploadedBlob: Blob | null = null;
  uploadedPreviewUrl: string | null = null;
  hasDrawing = false;
  isSaving = false;

  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;

  constructor(
    private readonly dialogRef: MatDialogRef<SignatureCaptureDialogComponent>,
    private readonly fileApiService: FileApiService,
    private readonly dialog: MatDialog,
    private readonly translateService: TranslocoService,
    @Inject(MAT_DIALOG_DATA) public data: SignatureCaptureDialogData
  ) {}

  ngOnInit(): void {
    if (this.data.existingSignatureImageId != null) {
      this.activeTab = UPLOAD_TAB_INDEX;
      this.fileApiService.getImageById(this.data.existingSignatureImageId).subscribe(blob => this.setUploadedBlob(blob));
    }
  }

  ngOnDestroy(): void {
    this.revokePreviewUrl();
  }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d')!;
    this.ctx.lineWidth = 2.5;
    this.ctx.lineCap = 'round';
    this.ctx.strokeStyle = '#1a1a1a';
  }

  setTab(tab: number): void {
    this.activeTab = tab;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    if (!this.allowedImageTypes.includes(file.type)) {
      this.showInvalidImageDialog(file.name);
      return;
    }
    this.setUploadedBlob(file);
  }

  private showInvalidImageDialog(imageName: string): void {
    const dialogData: SimpleDialogData = {
      title: imageName,
      messages: [this.translateService.translate('errors.imageFormatNotSupported')],
      icon: 'error',
    };
    this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(dialogData, '350px'));
  }

  removeUploadedImage(): void {
    this.setUploadedBlob(null);
  }

  private setUploadedBlob(blob: Blob | null): void {
    this.revokePreviewUrl();
    this.uploadedBlob = blob;
    this.uploadedPreviewUrl = blob ? URL.createObjectURL(blob) : null;
  }

  private revokePreviewUrl(): void {
    if (this.uploadedPreviewUrl) {
      URL.revokeObjectURL(this.uploadedPreviewUrl);
    }
  }

  startDrawing(event: PointerEvent): void {
    this.isDrawing = true;
    this.hasDrawing = true;
    const { x, y } = this.getPosition(event);
    this.lastX = x;
    this.lastY = y;
  }

  draw(event: PointerEvent): void {
    if (!this.isDrawing) {
      return;
    }
    const { x, y } = this.getPosition(event);
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.lastX = x;
    this.lastY = y;
  }

  stopDrawing(): void {
    this.isDrawing = false;
  }

  clearDrawing(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.hasDrawing = false;
  }

  selectPersistChoice(saveToProfile: boolean): void {
    this.saveToProfile = saveToProfile;
  }

  hasSignature(): boolean {
    return this.activeTab === UPLOAD_TAB_INDEX ? !!this.uploadedBlob : this.hasDrawing;
  }

  onCancel(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }

  onConfirm(): void {
    if (this.isSaving) {
      return;
    }
    if (!this.hasSignature()) {
      if (this.data.existingSignatureImageId == null) {
        return;
      }
      this.dialogRef.close({
        event: DialogActions.CONFIRM,
        data: { signatureImageId: null, signatureImage: null },
      });
      return;
    }
    if (this.activeTab === UPLOAD_TAB_INDEX) {
      this.finalizeSignature(this.uploadedBlob!);
      return;
    }
    this.canvasRef.nativeElement.toBlob(blob => {
      if (blob) {
        this.finalizeSignature(blob);
      }
    }, 'image/png');
  }

  private finalizeSignature(blob: Blob): void {
    const saveToProfile = !this.data.showPersistenceChoice || this.saveToProfile;

    if (!saveToProfile) {
      this.dialogRef.close({
        event: DialogActions.CONFIRM,
        data: { signatureImageId: null, signatureImage: blob },
      });
      return;
    }

    this.isSaving = true;
    const formData = new FormData();
    formData.append('files', blob);
    this.fileApiService.putImages(formData, true).subscribe({
      next: imageIds => {
        this.isSaving = false;
        this.dialogRef.close({
          event: DialogActions.CONFIRM,
          data: { signatureImageId: imageIds[0], signatureImage: null },
        });
      },
      error: () => {
        this.isSaving = false;
        const dialogData: SimpleDialogData = {
          title: this.translateService.translate('common.text.error'),
          messages: [this.translateService.translate('errors.serverCommunicationError')],
          icon: 'error',
        };
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(dialogData, '350px'));
      },
    });
  }

  private getPosition(event: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  }
}
